"use client";
import { Dialog, DialogHeader, DialogBody } from "@dxp-feed/ui/Dialog";
import { Text } from "@dxp-feed/ui/Text";
import { Box } from "@dxp-feed/ui/Box";
import ImageWrapper from "@/components/ImageWrapper";
import Link from "@/components/Link";
import { useGigya } from "@dxp-feed/gigya";
import { usePathname, useRouter } from "next/navigation";
import Loader from "@/components/Loader";
import React, { useEffect, useState } from "react";
import {
  handlePopupClose,
  shouldShowPopup,
  triggerVerifyCard,
} from "@/helpers/sceneCardFunctions";
import { useAppDispatch } from "@/utils/hooks/reactUseCustomHooks";
import { useLogin } from "@/helpers/useCommonFunctions";
import { addDataLayer } from "@/helpers/dataLayer";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { Button } from "@dxp-feed/ui";

const VerifyPopup = ({ disableSuppress }: { disableSuppress?: boolean }) => {
  const router = useRouter();
  const { loadLogin } = useLogin();
  const pathName = usePathname();
  const { gigya } = useGigya();
  const dispatch = useAppDispatch();
  const getGigyaToken = useGigyaAccountInfo();

  const [isOpen, setIsOpen] = useState(true);
  const [loader, setLoading] = useState(false);
  const closeDialog = () => setIsOpen(false);

  useEffect(() => {
    if (
      ["/verify-card", "/scene"].includes(pathName) ||
      !shouldShowPopup("link_suppress")
    ) {
      setIsOpen(false);
    }
  }, [isOpen, pathName]);

  return (
    <>
      {isOpen && (
        <Dialog
          isOpen={isOpen}
          onClose={closeDialog}
          className="xs:max-w-[464px] max-w-full xs:w-full w-[90%] mx-6 outline-none !bg-brandWhite !before:hidden !border-0 !hover:border-0 !focus:border-0 !hover:bg-brandWhite !focus:bg-brandWhite focus:text-brandBlack"
          closeOnOverlayClick={false}
        >
          {loader ? (
            <Loader className="h-full w-full bg-[rgba(255,255,255,0.5)] absolute left-0 top-0 mx-auto" />
          ) : (
            ""
          )}
          <DialogHeader className="justify-end">
            <Button
              className="text-brandBlack text-2xl hover:text-gray-800 !bg-brandWhite !before:hidden !border-0 !hover:border-0 !focus:border-0 !hover:bg-brandWhite !focus:bg-brandWhite focus:text-brandBlack"
              onClick={() => {
                if (!disableSuppress) {
                  handlePopupClose({
                    cookie: "link_suppress",
                    suppressType: "hard",
                  });
                }
                closeDialog();
                document.body.style.touchAction = "";
                addDataLayer({
                  event: "popup_card",
                  click_text: "Close",
                });
              }}
              aria-label="Close"
            >
              <Text as="span" className="icon-close flex w-4 text-base" />
            </Button>
          </DialogHeader>
          <DialogBody className="pb-0">
            <Box className="flex justify-center items-center pb-6 xs:w-[250px] w-[150px] mx-auto">
              <ImageWrapper
                src="/Verify-your-Scene-card.svg"
                className="object-cover mx-auto"
                alt="verify your Scene+ card"
                width={241}
                height={256}
                style={{ objectFit: "cover" }}
              />
            </Box>
            <h2 className="xs:text-2xl text-lg font-sans text-center font-bold pb-4">
              Verify your Scene+ card
            </h2>
            <Text as="p" className="text-body text-center">
              Scene+ card is added but not yet verified.
            </Text>

            <Box className="xs:flex gap-4 py-6">
              <Button
              variant="outline"
                onClick={() => {
                  closeDialog();
                  addDataLayer({
                    event: "popup_card",
                    click_text: "Remind Me Later",
                  });
                  if (!disableSuppress) {
                    handlePopupClose({
                      cookie: "link_suppress",
                      suppressType: "soft",
                    });
                  }
                }}
                className="buttonSelectStore text-[14px] !px-4 !border-[1px] !border-grey350 xs:w-1/2 w-full focus:!text-brandWhite"
                type="submit"
                aria-label="Remind Me Later"
              >
                Remind Me Later
              </Button>
              <Button
                className="buttonDirection text-[14px] !px-4 flex gap-3 justify-center items-center xs:w-1/2 w-full xs:mt-0 mt-4 !border-[1px]  !border-[#003D2A] focus:!text-[#003D2A]"
                type="submit"
                onClick={async () => {
                  const tokenResponse = await getGigyaToken();
                  await triggerVerifyCard({
                    dispatch,
                    gigya,
                    setLoading,
                    router,
                    pathName,
                    loadLogin,
                    callback: async () => {
                      setIsOpen(false);
                    },
                    tokenResponse,
                  });
                }}
                aria-label="Verify Card"
              >
                Verify Card
              </Button>
            </Box>
            <Text as="p" className="text-body text-center">
              Having trouble?{" "}
              <Link
                href={process.env.NEXT_PUBLIC_SCENE_SUPPORT_LINK || ""}
                className="text-body font-semibold text-[#003D2A] pb-6 underline"
                target="_blank"
              >
                Contact Scene+ Support
              </Link>
            </Text>
          </DialogBody>
        </Dialog>
      )}
    </>
  );
};

export default VerifyPopup;
